import { Children, createContext, useContext, type ReactNode } from 'react';

type Align = 'center' | 'end';

export type Column = {
  label?: ReactNode;
  align?: Align;
  /** `aria-sort` and the arrow, on the column the rows are sorted by. */
  sort?: 'ascending' | 'descending';
  /** Makes the header a button that sorts by this column. */
  onSort?: () => void;
  /** Hidden below md, header and cells alike. */
  priority?: 'secondary';
};

const ColumnsContext = createContext<Column[]>([]);
const IndexContext = createContext(0);

const cellClass = (align?: Align) =>
  align === 'center' ? 'cell-center' : align === 'end' ? 'cell-end' : 'cell';

const secondary = (column?: Column) => column?.priority === 'secondary';

/**
 * A table in a card. It stays a table at every width: below md the columns
 * marked `priority: 'secondary'` are hidden, and a table still too wide
 * scrolls sideways inside its card. `empty` fills the body when there are no
 * rows.
 */
export function DataTable({
  columns,
  empty,
  children,
}: {
  columns: Column[];
  empty?: ReactNode;
  children?: ReactNode;
}) {
  const hasRows = Children.toArray(children).length > 0;
  return (
    <div className="card-table">
      <table className="data-table">
        <thead className="data-table-head">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className={
                  secondary(column)
                    ? `${cellClass(column.align)} cell-secondary`
                    : cellClass(column.align)
                }
                aria-sort={column.sort}
              >
                {column.onSort ? (
                  <button
                    type="button"
                    className="sort-button"
                    onClick={column.onSort}
                  >
                    {column.label}
                    <span aria-hidden="true">
                      {column.sort === 'ascending'
                        ? '↑'
                        : column.sort === 'descending'
                          ? '↓'
                          : ''}
                    </span>
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <ColumnsContext.Provider value={columns}>
            {hasRows || empty === undefined ? (
              children
            ) : (
              <tr>
                <td className="cell-empty" colSpan={columns.length}>
                  {empty}
                </td>
              </tr>
            )}
          </ColumnsContext.Provider>
        </tbody>
      </table>
    </div>
  );
}

/**
 * A row. Each `Td` must be a direct child: its position is what ties it to
 * its column's alignment and priority.
 */
export function Tr({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <tr id={id} className="data-table-row">
      {Children.map(children, (child, index) => (
        <IndexContext.Provider value={index}>{child}</IndexContext.Provider>
      ))}
    </tr>
  );
}

export function Td({
  align,
  emphasis,
  numeric,
  colSpan,
  className,
  children,
}: {
  /** Overrides the column's alignment. */
  align?: Align;
  emphasis?: boolean;
  /** Tabular figures, so digits line up down the column. */
  numeric?: boolean;
  colSpan?: number;
  className?: string;
  children?: ReactNode;
}) {
  const column = useContext(ColumnsContext)[useContext(IndexContext)];
  const classes = [
    emphasis ? 'cell-emphasis' : cellClass(align ?? column?.align),
  ];
  if (secondary(column)) classes.push('cell-secondary');
  if (numeric) classes.push('cell-numeric');
  if (className) classes.push(className);
  return (
    <td className={classes.join(' ')} colSpan={colSpan}>
      {children}
    </td>
  );
}
